import React from 'react';
import { Box } from '@mui/material';

interface EmailPreviewProps {
  candidateName: string;
  testName: string;
  personalMessage?: string;
  managerName?: string;
  questionCount?: number;
}

const EmailPreview: React.FC<EmailPreviewProps> = ({ 
  candidateName, 
  testName, 
  personalMessage,
  managerName,
  questionCount
}) => {
  const interviewCode = 'ABC123-DEF456';
  const clientDownload = 'https://interviewzen.com/downloads';

  const emailStyles = {
    emailContainer: {
      maxWidth: '600px',
      margin: 'auto',
      backgroundColor: '#FFFFFF',
      padding: '20px',
      borderRadius: '8px',
      boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    },
    emailHeader: {
      textAlign: 'center' as const,
      backgroundColor: '#3399FF',
      color: 'white',
      padding: '20px',
      borderRadius: '8px 8px 0 0',
    },
    emailHeaderH1: {
      margin: 0,
      fontSize: '24px',
      fontWeight: 'bold',
    },
    emailBody: {
      textAlign: 'center' as const,
      padding: '20px',
    },
    emailBodyH2: {
      color: '#333',
      marginBottom: '15px',
    },
    emailBodyP: {
      color: '#555',
      fontSize: '16px',
      lineHeight: 1.6,
      textAlign: 'left' as const,
    },
    personalMessageBox: {
      fontStyle: 'italic' as const,
      backgroundColor: '#f0f8ff',
      padding: '15px',
      borderLeft: '4px solid #3399FF',
      margin: '15px 0',
    },
    interviewDetails: {
      background: '#F8F9FA',
      padding: '20px',
      borderRadius: '8px',
      margin: '20px 0',
      textAlign: 'left' as const,
    },
    interviewDetailsH3: {
      color: '#333',
      marginTop: 0,
    },
    interviewDetailsUl: {
      listStyleType: 'none' as const,
      padding: 0,
    },
    interviewDetailsLi: {
      margin: '10px 0',
      padding: '5px 0',
    },
    interviewCode: {
      display: 'inline-block',
      fontFamily: "'Courier New', Courier, monospace",
      fontSize: '20px',
      fontWeight: 'bold',
      letterSpacing: '1px',
      backgroundColor: '#EEF4FF',
      color: '#1a1a1a',
      padding: '12px 20px',
      margin: '8px 0',
      border: '2px dashed #3399FF',
      borderRadius: '6px',
      wordBreak: 'break-all' as const,
    },
    ctaButton: {
      display: 'inline-block',
      backgroundColor: '#3399FF',
      color: 'white',
      padding: '15px 30px',
      textDecoration: 'none',
      borderRadius: '5px',
      fontWeight: 'bold',
      margin: '20px 0',
    },
    clientDownloadBtn: {
      backgroundColor: '#28a745',
      color: 'white',
      padding: '12px 25px',
      textDecoration: 'none',
      borderRadius: '5px',
      fontWeight: 'bold',
      margin: '10px 0',
      display: 'inline-block',
    },
    instructions: {
      textAlign: 'left' as const,
      margin: '20px 0',
    },
    instructionsOl: {
      paddingLeft: '20px',
    },
    instructionsLi: {
      margin: '8px 0',
    },
    emailFooter: {
      textAlign: 'center' as const,
      marginTop: '30px',
      fontSize: '12px',
      color: '#777',
      lineHeight: 1.4,
    },
    emailFooterA: {
      color: '#3399FF',
      textDecoration: 'none',
    },
    smallText: {
      fontSize: '14px',
      color: '#666',
    },
  };

  return (
    <Box sx={{ 
      border: '1px solid #e0e0e0', 
      borderRadius: '8px', 
      overflow: 'hidden',
      backgroundColor: '#f5f5f5',
      p: 2,
      maxHeight: '600px',
      overflowY: 'auto'
    }}>
      <div style={emailStyles.emailContainer}>
        <div style={emailStyles.emailHeader}>
          <h1 style={emailStyles.emailHeaderH1}>Interview Zen</h1>
        </div>
        
        <div style={emailStyles.emailBody}>
          <h2 style={emailStyles.emailBodyH2}>
            Hello {candidateName || '[Candidate Name]'},
          </h2>
          <p style={emailStyles.emailBodyP}>
            You've been invited to complete a technical interview on <strong>Interview Zen</strong>. For more details about the platform, visit <a href="https://docs.interviewzen.com" style={emailStyles.emailFooterA}>docs.interviewzen.com</a>.
          </p>
          
          {personalMessage && (
            <div>
              <h3 style={{ ...emailStyles.emailBodyP, color: '#333', fontWeight: 'bold' }}>
                Message{managerName ? ` from ${managerName}` : ''}:
              </h3>
              <p style={{ ...emailStyles.emailBodyP, ...emailStyles.personalMessageBox }}>
                {personalMessage}
              </p>
            </div>
          )}
          
          <div style={emailStyles.interviewDetails}>
            <h3 style={emailStyles.interviewDetailsH3}>Interview Details</h3>
            <ul style={emailStyles.interviewDetailsUl}>
              <li style={emailStyles.interviewDetailsLi}>
                <strong>Test:</strong> {testName || '[Test Name]'}
              </li>
              {questionCount && questionCount > 0 && (
                <li style={emailStyles.interviewDetailsLi}>
                  <strong>Questions:</strong> {questionCount}
                </li>
              )}
              <li style={emailStyles.interviewDetailsLi}>
                <strong>Interview Code:</strong>
                <div style={emailStyles.interviewCode}>{interviewCode}</div>
                <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic', marginTop: '4px' }}>
                  *This code will be generated when the invitation is sent
                </div>
              </li>
            </ul>
          </div>

          <div style={emailStyles.instructions}>
            <h3 style={emailStyles.interviewDetailsH3}>Getting Started</h3>
            <ol style={emailStyles.instructionsOl}>
              <li style={emailStyles.instructionsLi}>
                Download and install Interview Zen:
                <br /><a href={clientDownload} style={emailStyles.clientDownloadBtn}>Download Interview Zen</a>
              </li>
              <li style={emailStyles.instructionsLi}>
                Open the app and enter your interview code:
                <div style={emailStyles.interviewCode}>{interviewCode}</div>
              </li>
              <li style={emailStyles.instructionsLi}>Grant the required permissions (screen recording, camera)</li>
              <li style={emailStyles.instructionsLi}>Answer each question to the best of your ability</li>
              <li style={emailStyles.instructionsLi}>Submit when you're done</li>
            </ol>
          </div>

          <p style={{ textAlign: 'center', margin: '10px 0' }}>
            <span style={{ color: '#777', fontSize: '14px' }}>Already have Interview Zen installed?</span><br />
            <a href="#" style={emailStyles.ctaButton}>Launch Interview</a>
          </p>

          <p style={{ ...emailStyles.smallText, marginTop: '20px' }}>
            This interview includes screen recording and periodic webcam snapshots so your interviewer can verify your identity and review your session. For full details, see our <a href="https://docs.interviewzen.com/candidates" style={emailStyles.emailFooterA}>candidate guide</a>.
          </p>

          <p style={emailStyles.smallText}>
            If you run into any issues, visit our <a href="https://docs.interviewzen.com/troubleshooting" style={emailStyles.emailFooterA}>troubleshooting page</a> or contact your interview coordinator.
          </p>
        </div>
        
        <div style={emailStyles.emailFooter}>
          <p>&copy; 2025 Interview Zen | <a href="https://interviewzen.com" style={emailStyles.emailFooterA}>www.interviewzen.com</a></p>
          <p>Support: <a href="mailto:support@interviewzen.com" style={emailStyles.emailFooterA}>support@interviewzen.com</a></p>
          <p><small>This is an automated message. Please do not reply to this email.</small></p>
        </div>
      </div>
    </Box>
  );
};

export default EmailPreview;
